import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "../utils/axios";
import { useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import Config from "../utils/config";
import { FaFilter } from "react-icons/fa";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

function Dashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [inputValues, setInputValues] = useState({
    firstName: "",
    lastName: "",
    minAge: "18",
    maxAge: "70",
    sexe: "",
    ville: "",
    telephone: "",
    email: "",
    nationality: "",
    pays: "",
    status: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (filters = {}) => {
    try {
      const response = await axios.get(`${Config.baseURL}/get-all`, {
        params: filters,
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const headings = [
    { key: "createdAt", value: "Date de création" },
    { key: "firstName", value: "Prénom" },
    { key: "lastName", value: "Nom" },
    { key: "dateNaissance", value: "Age" },
    { key: "sexe", value: "Genre" },
    { key: "ville", value: "Ville" },
    { key: "nationality", value: "Nationalité" },
    { key: "status", value: "Status" },
  ];

  const calculateAge = (dateOfBirth) => {
    const currentDate = new Date();
    const targetDate = new Date(dateOfBirth);

    const currentYear = currentDate.getFullYear();
    const targetYear = targetDate.getFullYear();

    const yearDifference = currentYear - targetYear;

    if (
      currentDate.getMonth() < targetDate.getMonth() ||
      (currentDate.getMonth() === targetDate.getMonth() &&
        currentDate.getDate() < targetDate.getDate())
    ) {
      return yearDifference - 1;
    }

    return yearDifference;
  };

  const getRowDetail = (event, id) => {
    event.preventDefault();
    navigate(`/details/${id}`);
  };

  const handleFilterClick = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setInputValues({
      ...inputValues,
      [name]: value.trim(),
    });
  };

  const handleDelete = async (event, id) => {
    event.preventDefault();
    const confirmDelete = window.confirm(
      "Voulez-vous vraiment supprimer cet candidat ?"
    );
    if (confirmDelete) {
      try {
        await axios.delete(`${Config.baseURL}/delete/${id}`);
        fetchData();
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };

  const buildFilterParams = () => {
    const params = {};
    if (inputValues.firstName) params.firstName = inputValues.firstName;
    if (inputValues.lastName) params.lastName = inputValues.lastName;
    if (inputValues.sexe) params.sexe = inputValues.sexe;
    if (inputValues.ville) params.ville = inputValues.ville;
    if (inputValues.telephone) params.telephone = inputValues.telephone;
    if (inputValues.email) params.email = inputValues.email;
    if (inputValues.nationality) params.nationality = inputValues.nationality;
    if (inputValues.pays) params.pays = inputValues.pays;
    if (inputValues.status) params.status = inputValues.status;
    if (inputValues.minAge) params.minAge = inputValues.minAge;
    if (inputValues.maxAge) params.maxAge = inputValues.maxAge;
    return params;
  };

  const handleFilter = () => {
    fetchData(buildFilterParams());
    setIsFilterModalOpen(false);
  };

  const handleClearFilters = () => {
    setInputValues({
      firstName: "",
      lastName: "",
      minAge: "18",
      maxAge: "70",
      sexe: "",
      ville: "",
      telephone: "",
      email: "",
      nationality: "",
      pays: "",
      status: "",
    });
    fetchData();
    setIsFilterModalOpen(false);
  };

  const handleMenuClick = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  if (loading) {
    return <Spinner heading="Chargement..." />;
  }

  const hasActiveFilters =
    inputValues.firstName ||
    inputValues.lastName ||
    inputValues.sexe ||
    inputValues.ville ||
    inputValues.telephone ||
    inputValues.email ||
    inputValues.nationality ||
    inputValues.pays ||
    inputValues.status ||
    inputValues.minAge !== "18" ||
    inputValues.maxAge !== "70";

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 w-full min-w-0">
        {/* Header + quick filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Recrutements</h1>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <select
              name="status"
              value={inputValues.status}
              onChange={(e) => {
                const newStatus = e.target.value;
                setInputValues((prev) => ({ ...prev, status: newStatus }));
                fetchData({ ...buildFilterParams(), status: newStatus });
              }}
              className="px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white min-w-0 w-full sm:w-auto sm:min-w-[140px] min-h-[44px] sm:min-h-0"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En cours</option>
              <option value="rejected">Rejeté</option>
              <option value="approved">Approuvé</option>
            </select>
            <button
              type="button"
              onClick={handleFilterClick}
              className="inline-flex items-center justify-center px-4 py-3 sm:py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-h-[44px] sm:min-h-0"
            >
              <FaFilter className="mr-2 w-4 h-4 flex-shrink-0" />
              Filtres avancés
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 min-h-[44px] sm:min-h-0"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Advanced filter modal - portaled to body so overlay covers full viewport */}
        {isFilterModalOpen &&
          ReactDOM.createPortal(
            <div
              className="fixed inset-0 flex items-center justify-center"
              style={{ zIndex: 9999 }}
            >
              <div
                className="fixed inset-0 bg-black/50"
                style={{ top: 0, left: 0, right: 0, bottom: 0 }}
                onClick={handleCloseFilterModal}
                aria-hidden="true"
              />
              <div className="relative bg-white max-w-lg w-full mx-3 sm:mx-4 p-4 sm:p-6 rounded-xl shadow-xl z-10 max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Filtres avancés</h2>
              <button
                type="button"
                onClick={handleCloseFilterModal}
                className="p-2.5 rounded-lg text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fermer"
              >
              <svg
                className="w-6 h-6 text-gray-600 cursor-pointer"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-4 sm:me-4">
              <input
                type="text"
                name="firstName"
                value={inputValues.firstName}
                onChange={handleInputChange}
                placeholder="Prénom"
                className="w-full px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 min-h-[44px] sm:min-h-0"
              />
              <input
                type="text"
                name="lastName"
                value={inputValues.lastName}
                onChange={handleInputChange}
                placeholder="Nom"
                className="w-full px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 min-h-[44px] sm:min-h-0"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 sm:justify-between sm:items-center">
              <input
                type="number"
                name="minAge"
                value={inputValues.minAge}
                onChange={handleInputChange}
                placeholder="Âge min"
                className="w-full sm:w-1/2 px-4 py-2.5 sm:py-2 me-0 sm:me-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 min-h-[44px] sm:min-h-0"
              />
              <input
                type="number"
                name="maxAge"
                value={inputValues.maxAge}
                onChange={handleInputChange}
                placeholder="Âge max"
                className="w-full sm:w-1/2 px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 min-h-[44px] sm:min-h-0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sexe
              </label>
              <div className="flex gap-4">
                <div>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio h-5 w-5 text-blue-600"
                      name="sexe"
                      value="Homme"
                      checked={inputValues.sexe === "Homme"}
                      onChange={handleInputChange}
                    />
                    <span className="ml-2">Homme</span>
                  </label>
                  <label className="inline-flex ms-6 items-center mt-2">
                    <input
                      type="radio"
                      className="form-radio h-5 w-5 text-blue-600"
                      name="sexe"
                      value="Femme"
                      checked={inputValues.sexe === "Femme"}
                      onChange={handleInputChange}
                    />
                    <span className="ml-2">Femme</span>
                  </label>
                </div>
              </div>
            </div>

            <input
              type="text"
              name="telephone"
              value={inputValues.telephone}
              onChange={handleInputChange}
              placeholder="Téléphone"
              className="w-full px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 min-h-[44px] sm:min-h-0"
            />

            <input
              type="email"
              name="email"
              value={inputValues.email}
              onChange={handleInputChange}
              placeholder="E-mail"
              className="w-full px-4 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 min-h-[44px] sm:min-h-0"
            />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select
              name="status"
              value={inputValues.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[44px] sm:min-h-0"
            >
              <option value="">Tous</option>
              <option value="pending">En cours</option>
              <option value="rejected">Rejeté</option>
              <option value="approved">Approuvé</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              type="button"
              onClick={handleFilter}
              className="flex-1 py-3 sm:py-2.5 px-4 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-h-[44px] sm:min-h-0"
            >
              Appliquer
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              className="py-3 sm:py-2.5 px-4 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 focus:outline-none min-h-[44px] sm:min-h-0"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>,
            document.body
          )}

        {/* Table - horizontal scroll on small screens only */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden w-full min-w-0">
        <div className="overflow-x-auto overflow-y-hidden -webkit-overflow-scrolling-touch" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full min-w-[640px] border-collapse table-auto">
            <thead className="bg-slate-50">
              <tr className="text-left text-sm">
                <th className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-700 whitespace-nowrap">
                  #
                </th>
                {headings.map((heading) => (
  <th
    key={heading.key}
    className={`px-4 py-3 border-b border-gray-200 font-semibold text-gray-700 tracking-wider whitespace-nowrap ${
      heading.key === 'status' ? 'min-w-[100px]' : ''
    }`}
  >
    {heading.value}
  </th>
))}
                <th className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-700 whitespace-nowrap text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100 text-sm">
              {users.map((user, index) => (
                <tr key={user._id} className={index % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50/50 hover:bg-gray-50"}>
                  <td className="px-4 py-3 font-medium text-gray-700 whitespace-nowrap">
                    {users.length - index}
                  </td>
                  {headings.map((heading) => (
                    <td
                      key={`${user._id}-${heading.key}`}
                      className="px-4 py-3 font-medium text-gray-700"
                    >
                      {heading.key === "dateNaissance" ? (
                        calculateAge(user[heading.key])
                      ) : heading.key === "status" ? (
                        <span
                          className={`inline-block text-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColorClass(
                            user[heading.key]
                          )}`}
                        >
                          {getStatusFrenchName(user[heading.key])}
                        </span>
                      ) : heading.key === "createdAt" ? (
                        <span>
                          {new Date(user[heading.key]).toLocaleDateString(
                            "fr-FR"
                          )}
                        </span>
                      ) : (
                        user[heading.key]
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      aria-controls="simple-menu"
                      aria-haspopup="true"
                      onClick={(event) => handleMenuClick(event, user._id)}
                      className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center font-normal text-md rounded-full text-gray-500 bg-gray-100 hover:text-gray-600"
                    >
                      <MoreVertIcon />
                    </button>
                    <Menu
                      id="simple-menu"
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl && selectedUserId === user._id)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem
                        onClick={(event) => {
                          getRowDetail(event, user._id);
                          handleMenuClose();
                        }}
                      >
                        Voir
                      </MenuItem>
                      <MenuItem
                        onClick={(event) => {
                          handleDelete(event, user._id);
                          handleMenuClose();
                        }}
                      >
                        Supprimer
                      </MenuItem>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      </div>
    </div>
  );
}

const getStatusColorClass = (status) => {
  switch (status) {
    case "pending":
      return "bg-amber-500 text-white";
    case "rejected":
      return "bg-red-500 text-white";
    case "approved":
      return "bg-emerald-600 text-white";
    default:
      return "bg-gray-400 text-white";
  }
};

const statusFrenchNames = {
  pending: "En cours",
  rejected: "Rejetée",
  approved: "Approuvée",
};

const getStatusFrenchName = (key) => {
  return statusFrenchNames[key] || "Inconnu";
};

export default Dashboard;
